/**
 * The address a practitioner's shared forms answer at —
 * `/api/v1/enquiry-connect-link/` — read when the Form tab opens, checked as a
 * slug is typed, and claimed when the link is copied.
 *
 * The backend calls it a *connect link* and this screen calls it a form link;
 * the wire names below are the backend's, and everything above this file uses
 * the screen's. That is the one place the two vocabularies meet.
 *
 * One field is written and three come back, and the one that matters most is
 * `share_url`: the whole address, assembled by the server. This app is never
 * the authority on it. It has a template to draw a box with before anything
 * has been claimed, but what gets copied is always what the backend said, so a
 * link cannot be handed to a client in a shape the server does not serve.
 *
 * What is not short is the failure this exists to report: the slug is taken out
 * of a namespace shared with every other workspace, so a save can fail for a
 * reason no other save on this screen can — somebody else got there first — and
 * that has to arrive back at the field rather than in a toast over the top of
 * it.
 */

import { ApiError, api, API_ENDPOINTS } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import type { ConnectLink } from "../types/brand.types";

/** `{ connect_link: { … } }`, which both `GET` and `PATCH` answer with. */
interface ConnectLinkResponse {
  connect_link: {
    slug: string;
    is_active: boolean;
    share_url: string;
  };
}

interface AvailabilityResponse {
  slug: string;
  available: boolean;
}

/** `{ slug }` in and `{ slug }` back — see `savePracticeDomain`. */
interface PracticeDomainResponse {
  slug?: string | null;
}

const toConnectLink = (response: ConnectLinkResponse): ConnectLink => ({
  slug: response.connect_link.slug,
  isActive: response.connect_link.is_active,
  shareUrl: response.connect_link.share_url,
});

/**
 * The link already claimed, or `null` where there is none.
 *
 * A 404 is read as "none yet" rather than as a failure: a workspace that has
 * never opened this tab has no record to read, and an empty box is the right
 * screen for that — the same reading `fetchBrandProfile` gives a brand that has
 * never been saved.
 */
export const fetchConnectLink = async (
  getToken: GetToken,
  signal?: AbortSignal
): Promise<ConnectLink | null> => {
  try {
    const response = await api.get<ConnectLinkResponse>(
      API_ENDPOINTS.enquiries.connectLink,
      { token: await getToken(), signal }
    );
    return response.connect_link ? toConnectLink(response) : null;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
};

/**
 * Whether a slug is free, asked while it is being typed.
 *
 * An answer and not a promise. It is true when it is given and can be stale a
 * second later, so nothing here reserves anything — `saveConnectLink` is what
 * decides, and a rejection from it is handled whatever this said.
 */
export const checkSlugAvailable = async (
  getToken: GetToken,
  slug: string,
  signal?: AbortSignal
): Promise<boolean> => {
  const response = await api.get<AvailabilityResponse>(
    API_ENDPOINTS.enquiries.connectLinkAvailability,
    { token: await getToken(), query: { slug }, signal }
  );
  return response.available;
};

/**
 * Claims the slug, and answers with the link as the backend now holds it —
 * including the address it will actually be served at.
 *
 * The response rather than what was sent, for the reason every save on this
 * screen takes the response: the backend normalises as it stores, and the box
 * has to show what is actually reachable.
 */
export const saveConnectLink = async (
  getToken: GetToken,
  slug: string
): Promise<ConnectLink> => {
  const response = await api.patch<ConnectLinkResponse>(
    API_ENDPOINTS.enquiries.connectLink,
    { slug: slug.trim() },
    { token: await getToken() }
  );
  return toConnectLink(response);
};

/**
 * Tells the pre-consultation service which subdomain to serve this
 * workspace's client links on, and answers with the slug as it stored it.
 *
 * The second half of claiming a name. The slug is one address — the enquiry
 * form at `/form` on it and a client's pre-consultation form at `/i/<token>` —
 * but two services hold it, and the one that mints those tokens has to be told
 * the name before it can build a link with it in.
 *
 * It takes the slug `saveConnectLink` above stored rather than the one that
 * was typed: the backend normalises as it saves, and the two records have to
 * name the same host. `useFormLink` is what sequences that.
 *
 * Nothing here catches. A failure is the caller's to weigh, and it weighs
 * differently from a failed claim: the link is already claimed and already
 * copyable by the time this runs, so this failing is not a reason to tell
 * somebody their link was not saved.
 */
export const savePracticeDomain = async (
  getToken: GetToken,
  slug: string
): Promise<string> => {
  const trimmed = slug.trim();
  const response = await api.patch<PracticeDomainResponse>(
    API_ENDPOINTS.preconsultation.practiceDomain,
    { slug: trimmed },
    { token: await getToken() }
  );
  // What came back, and what was sent where the response said nothing — the
  // return is only read for logging, and a blank there would say less than the
  // name that was registered.
  return (response?.slug ?? "").trim() || trimmed;
};

/**
 * The server's own objection to the slug, pulled out of whichever envelope it
 * came in — `{ details: { slug: [...] } }`, which is this API's shape, or a
 * plain `{ slug: [...] }`, which is DRF's underneath it.
 *
 * `undefined` for anything that is not about the slug: a 500, a dropped
 * connection, a session that expired. Those are not the field's fault and must
 * not be printed under it as though the practitioner had typed something wrong
 * — the caller shows them as a toast instead.
 */
export const takenSlugError = (error: unknown): string | undefined => {
  if (!(error instanceof ApiError)) return undefined;
  if (error.status !== 400 && error.status !== 409) return undefined;

  const body = error.data;
  if (!body || typeof body !== "object") return undefined;

  const envelope = body as { details?: unknown; slug?: unknown };
  const details =
    envelope.details && typeof envelope.details === "object"
      ? (envelope.details as { slug?: unknown })
      : envelope;

  const value = details.slug;
  const text = Array.isArray(value) ? value[0] : value;
  return typeof text === "string" && text.trim() ? text.trim() : undefined;
};
